import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";

export const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
export const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

// On native iOS the WKWebView process can be killed in the background, which
// wipes sessionStorage and forces a login on every app resume. Use localStorage
// on native so the session survives process restarts — same behaviour as any
// other native app. sessionStorage is still used on web (tab-close = logout).
const authStorage = Capacitor.isNativePlatform() ? localStorage : sessionStorage;

// Safari 26.5 beta crashes in WebKit's lock registry (WebLockRegistryProxy::requestLock)
// when navigator.locks.request() is called after the page has been open for a while.
// Since we use sessionStorage there is no cross-tab coordination needed, so a simple
// promise-chain mutex is sufficient and avoids the Navigator Locks API entirely.
const _locks = {};
async function processLock(name, _acquireTimeout, fn) {
  const prev = (_locks[name] ?? Promise.resolve()).catch(() => {});
  const current = prev.then(() => fn());
  _locks[name] = current.catch(() => {});
  return current;
}

export const supabaseClient = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    storage: authStorage,
    persistSession: true,
    detectSessionInUrl: false,
    autoRefreshToken: true,
    lock: processLock,
  },
});

// Cache the token in memory — updated by onAuthStateChange so it's always current.
// Avoids calling getSession() (which can hit the network) on every DB fetch.
let _cachedToken = null;
supabaseClient.auth.onAuthStateChange((_event, session) => {
  _cachedToken = session?.access_token ?? null;
});

export async function getAuthToken() {
  if (_cachedToken) {
    // Guard against stale token (e.g. tab slept past expiry).
    // Allow 30s buffer so requests don't go out with a token that's about to expire.
    try {
      const base64 = _cachedToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const { exp } = JSON.parse(atob(base64));
      if (exp * 1000 > Date.now() + 30_000) return _cachedToken;
    } catch (_) { /* malformed token — fall through to refresh */ }
  }
  // Fallback: ask Supabase for the current (possibly refreshed) session
  const { data } = await supabaseClient.auth.getSession();
  _cachedToken = data?.session?.access_token ?? null;
  return _cachedToken;
}

// For the one pre-auth fetch (SCCO list on login screen) — anon key only, no JWT required.
export function getAnonHeaders() {
  return {
    "Content-Type": "application/json",
    "apikey":       SUPABASE_KEY,
    "Authorization": "Bearer " + SUPABASE_KEY,
  };
}

// Returns an ISO date string (YYYY-MM-DD) for `days` days ago — shared by purge/load helpers.
export function cutoffDateISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export async function getDbHeaders(extra = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error("No auth session.");
  return {
    "Content-Type":  "application/json",
    "apikey":        SUPABASE_KEY,
    "Authorization": "Bearer " + token,
    "Prefer":        "return=minimal",
    ...extra,
  };
}
